import { useTranslation } from 'react-i18next'

import { OrganisationDetailsPanelOrganisationFragment } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { Fallback } from '@/modules/shared/components/fallback/Fallback'
import { PPNewDetailLink } from '@/modules/shared/components/ppnew-detail-link/PPNewDetailLink'
import { SimpleTableItemWithTooltip } from '@/modules/shared/components/simple-table-item/SimpleTableItemWithTooltip'
import { BooleanItem } from '@/modules/shared/components/table/table-column-items/shared/BooleanItem'
import { useMoney } from '@/modules/shared/hooks/useMoney'
import { useViewType } from '@/modules/shared/hooks/useViewType'
import { Dates } from '@/modules/shared/types/Dates'
import { formatDate } from '@/modules/shared/utils/formatDate'

interface OrganisationDetailsPanelProps {
  organisation: OrganisationDetailsPanelOrganisationFragment | null
}

export const OrganisationDetailsPanel = ({ organisation }: OrganisationDetailsPanelProps) => {
  const { t } = useTranslation()
  const { currency } = useMoney()
  const { isUtility } = useViewType()

  const { id, parent, legalEntity, createdAt, isASupplier, isVerified, owner, nearestLegalEntity } = organisation || {}

  return (
    <section className="flex w-full flex-col gap-y-4 text-sm xl:w-96">
      <section className="divide-y rounded-md bg-white shadow" data-testid="organisation-details">
        <h2 className="px-5 py-4 text-base font-bold">{t('general.organisationDetails', 'Organisation Details')}</h2>
        <div className="flex flex-col gap-y-3 p-5 text-sm">
          <SimpleTableItemWithTooltip
            title={t('general.organisationId', 'Organisation ID')}
            value={String(id)}
            tooltip={t(
              'adminCenter.details.organisationIdTooltip',
              'The unique identifier for this Organisation in PurchasePlus.'
            )}
          />
          <SimpleTableItemWithTooltip
            title={t('general.organisationType', 'Organisation Type')}
            value={isASupplier ? t('general.supplier', 'Supplier') : t('general.purchaser', 'Purchaser')}
            tooltip={t(
              'adminCenter.details.organisationTypeTooltip',
              'Indicates if the Organisation is a Purchaser or a Supplier.'
            )}
          />
          {isUtility && (
            <SimpleTableItemWithTooltip
              title={t('general.ownerOrganisation', 'Owner Organisation')}
              value={
                <Fallback condition={!!owner}>
                  <PPNewDetailLink type="utility-organisation" id={String(owner?.id)}>
                    {owner?.name}
                  </PPNewDetailLink>
                </Fallback>
              }
              tooltip={
                isASupplier
                  ? t(
                      'adminCenter.details.ownerOrganisationSupplierTooltip',
                      'If the Supplier Organisation is to be Self Managed by a Purchaser, then the Owner will be the Purchaser Organisation. If the Supplier is to be a Registered Supplier, then set the Owner here should be PurchasePlus. Refer to the PurchasePlus Configuration and Implementation Handbook for more information.'
                    )
                  : t(
                      'adminCenter.details.ownerOrganisationPurchaserTooltip',
                      'For Purchaser Organisations, the concept of ownership does not apply. The Owner here should be PurchasePlus. Refer to the PurchasePlus Configuration and Implementation Handbook for more information.'
                    )
              }
            />
          )}
          <SimpleTableItemWithTooltip
            title={t('general.parentOrganisation', 'Parent Organisation')}
            value={
              isUtility && parent ? (
                <Fallback condition={!!parent}>
                  <PPNewDetailLink type="utility-organisation" id={String(parent?.id)}>
                    {parent?.name}
                  </PPNewDetailLink>
                </Fallback>
              ) : (
                parent?.name
              )
            }
            tooltip={t(
              'adminCenter.details.parentOrganisationTooltip',
              'If the Organisation has a Parent, the name of the Parent Organisation will appear here.'
            )}
          />

          {isUtility && (
            <SimpleTableItemWithTooltip
              title={t('general.nearestLegalEntity', 'Nearest Legal Entity')}
              value={
                <Fallback condition={!!nearestLegalEntity}>
                  <PPNewDetailLink type="utility-organisation" id={String(nearestLegalEntity?.id)}>
                    {nearestLegalEntity?.name}
                  </PPNewDetailLink>
                </Fallback>
              }
              tooltip={t(
                'adminCenter.details.nearestLegalEntityTooltip',
                'The Nearest Legal Entity of this Organisation. If this Organisation is not itself a Legal Entity, then this will be the nearest Organisation above it in the hierarchy that is a Legal Entity.'
              )}
            />
          )}
          <SimpleTableItemWithTooltip
            title={t('general.legalEntity', 'Legal Entity')}
            value={
              isUtility ? (
                <BooleanItem value={!!legalEntity} />
              ) : legalEntity ? (
                t('general.yes', 'Yes')
              ) : (
                t('general.no', 'No')
              )
            }
            tooltip={t(
              'adminCenter.details.legalEntityTooltip',
              'Indicates if the Organisation is a Legal Entity or not. In PurchasePlus, a Legal Entity is treated differently to a Non-Legal Entity.'
            )}
          />

          {isUtility && (
            <SimpleTableItemWithTooltip
              title={t('general.isVerified', 'Is Verified')}
              value={<BooleanItem value={!!isVerified} />}
              tooltip={
                isASupplier
                  ? t(
                      'adminCenter.details.isVerifiedSupplierTooltip',
                      'Typically, Verified will be Yes for a Registered Supplier, and No if the Supplier is to be Self Managed by a Purchaser. Refer to the PurchasePlus Configuration and Implementation Handbook for more information.'
                    )
                  : t(
                      'adminCenter.details.isVerifiedPurchaserTooltip',
                      'Typically all Purchaser Organisations will be set to Verified. Refer to the PurchasePlus Configuration and Implementation Handbook for more information.'
                    )
              }
            />
          )}
          {!isASupplier && (
            <SimpleTableItemWithTooltip
              title={t('general.currency', 'Currency')}
              tooltip={t(
                'adminCenter.customise.currentCurrencyTooltip',
                'The currency that is currently set for this Organisation. Currency is set in the Accounts Payable system settings that apply to this Organisation.'
              )}
              value={currency}
            />
          )}
          <SimpleTableItemWithTooltip
            title={t('general.createdOn', 'Created On')}
            value={formatDate(Dates.Long, createdAt)}
            tooltip={t('adminCenter.details.createdOnTooltip', 'The time and date that this Organisation was created.')}
          />
        </div>
      </section>
    </section>
  )
}
